/**
 * CRM Opportunity Card
 *
 * Draggable card component for opportunities on the Kanban board
 */

import { memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Building2, Calendar, DollarSign, Activity, MoreVertical, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatCurrency } from '@/lib/utils';
import { getStageDefinition, OPPORTUNITY_TYPES, DEFAULT_CURRENCY } from '../../constants';
import type { PipelineOpportunity } from '../../types';

interface CrmOpportunityCardProps {
  opportunity: PipelineOpportunity;
  onView?: (opportunity: PipelineOpportunity) => void;
  onAddActivity?: (opportunity: PipelineOpportunity) => void;
  onCreateQuote?: (opportunity: PipelineOpportunity) => void;
  onMarkLost?: (opportunity: PipelineOpportunity) => void;
  isDragging?: boolean;
}

export const CrmOpportunityCard = memo(function CrmOpportunityCard({
  opportunity,
  onView,
  onAddActivity,
  onCreateQuote,
  onMarkLost,
  isDragging = false,
}: CrmOpportunityCardProps) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: opportunity.id,
    data: { opportunity },
  });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
      }
    : undefined;

  const stageDef = getStageDefinition(opportunity.stage);
  const typeDef = OPPORTUNITY_TYPES.find((t) => t.key === opportunity.type);

  // Calculate days until close
  const daysUntilClose = opportunity.target_close_date
    ? Math.ceil(
        (new Date(opportunity.target_close_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
    : null;

  // Priority indicator color
  const priorityColor =
    opportunity.priority === 'high'
      ? 'border-l-red-500'
      : opportunity.priority === 'medium'
        ? 'border-l-amber-500'
        : 'border-l-blue-500';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'bg-white rounded-lg border border-gray-200 shadow-sm p-3 cursor-grab active:cursor-grabbing',
        'hover:shadow-md transition-shadow duration-200',
        'border-l-4',
        priorityColor,
        isDragging && 'opacity-50 shadow-lg ring-2 ring-primary/20'
      )}
      onClick={() => onView?.(opportunity)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-sm text-gray-900 line-clamp-2 flex-1">
          {opportunity.title}
        </h4>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => onView?.(opportunity)}>View Details</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddActivity?.(opportunity)}>
              Log Activity
            </DropdownMenuItem>
            {opportunity.stage === 'proposal_presented' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onCreateQuote?.(opportunity)}
                  className="flex items-center gap-2 text-green-600 font-medium"
                >
                  <FileText className="h-4 w-4" />
                  Create Quote
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onMarkLost?.(opportunity)}
              className="text-red-600"
            >
              Mark as Lost
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Account */}
      {opportunity.account_name && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
          <Building2 className="h-3 w-3" />
          <span className="truncate">{opportunity.account_name}</span>
        </div>
      )}

      {/* Type badge */}
      {typeDef && (
        <Badge
          variant="outline"
          className="text-xs mb-2"
          style={{ borderColor: typeDef.color, color: typeDef.color }}
        >
          {typeDef.label}
        </Badge>
      )}

      {/* Value & Probability */}
      {opportunity.estimated_value != null && (
        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 mb-2">
          <DollarSign className="h-3.5 w-3.5 text-gray-400" />
          {formatCurrency(opportunity.estimated_value, opportunity.currency || DEFAULT_CURRENCY)}
          {opportunity.probability != null && opportunity.probability > 0 && (
            <span className="text-xs text-gray-500 ml-1">({opportunity.probability}%)</span>
          )}
        </div>
      )}

      {/* Footer: Close date & Activity count */}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
        {/* Close date */}
        {daysUntilClose !== null && (
          <div
            className={cn(
              'flex items-center gap-1',
              daysUntilClose < 0 && 'text-red-500',
              daysUntilClose <= 7 && daysUntilClose >= 0 && 'text-amber-500'
            )}
          >
            <Calendar className="h-3 w-3" />
            {daysUntilClose < 0
              ? `${Math.abs(daysUntilClose)}d overdue`
              : daysUntilClose === 0
                ? 'Today'
                : `${daysUntilClose}d`}
          </div>
        )}

        {/* Activity count */}
        {opportunity.activity_count !== undefined && opportunity.activity_count > 0 && (
          <div className="flex items-center gap-1">
            <Activity className="h-3 w-3" />
            {opportunity.activity_count}
          </div>
        )}

        {/* HubSpot badge */}
        {opportunity.hubspot_deal_id && (
          <Badge variant="outline" className="text-xs px-1 py-0">
            HS
          </Badge>
        )}
      </div>

      {/* Days in stage indicator */}
      {opportunity.days_in_stage !== undefined && opportunity.days_in_stage > 14 && (
        <div className="mt-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
          {opportunity.days_in_stage} days in {stageDef?.label || 'stage'}
        </div>
      )}
    </div>
  );
});
